import React, { useState } from "react";
import { Avatar, Box, ButtonBase, Chip, Divider, Menu, Stack, Tooltip, Typography } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { getAvatarColor, getInitials } from "./livechatDisplayUtils";
import { LEAD_PRODUCT_ALL } from "./livechatConstants";
import { PASTEL } from "./livechatTheme";
import Global from "../../customlib/Global";

// User & App Profile yang dipakai login. App Profile-nya sama dengan yang dikirim di header
// x-app-profile-id / auth socket (livechatApi.js, livechatSocket.js), jadi yang tampil di sini
// memang profil yang menentukan brand mana yang terlihat.
export function getLoginIdentity() {
    const user = Global.get()?.user || {};
    const appProfile = Global.getAppProfile() || {};
    return {
        userName: user.name || "-",
        email: user.email || null,
        role: user.role || null,
        appProfileLabel: appProfile.label || "-",
        appProfileWaNumber: appProfile.waNumber || null,
        appProfileType: appProfile.type || null,
    };
}

function InfoRow({ label, children }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.4}>{label}</Typography>
            {children}
        </Box>
    );
}

// Blok identitas di AppBar CrmLiveChat: nama user + App Profile selalu terlihat (desktop), dan
// sekaligus pemicu menu aplikasi. `children` = item menu (Kelola Sales, Settings, Logout) yang
// tetap diatur index.jsx.
export default function AgentIdentityMenu({ brands = [], availability = {}, compact = false, children }) {
    const [anchor, setAnchor] = useState(null);
    const close = () => setAnchor(null);
    const identity = getLoginIdentity();
    const avatarColor = getAvatarColor(identity.userName);
    const isSpecialist = availability.member && availability.leadProduct && availability.leadProduct !== LEAD_PRODUCT_ALL;

    const avatar = (size) => (
        <Avatar sx={{ width: size, height: size, fontSize: size * 0.4, fontWeight: 700, bgcolor: avatarColor }}>
            {getInitials(identity.userName)}
        </Avatar>
    );

    return (
        <>
            {/* Title dikosongkan saat menu terbuka: popper tooltip (z-index 1500) di atas Menu (1300),
                kalau tetap tampil ia menutupi bagian atas menu. */}
            <Tooltip title={anchor ? "" : `${identity.userName} · ${identity.appProfileLabel}`}>
                <ButtonBase
                    onClick={(e) => setAnchor(e.currentTarget)}
                    aria-label="Menu akun"
                    aria-controls={anchor ? "livechat-app-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={anchor ? "true" : undefined}
                    sx={{
                        display: "flex", alignItems: "center", gap: 1, borderRadius: 2,
                        px: compact ? 0.25 : 1, py: 0.5, flexShrink: 0,
                        "&:hover": { backgroundColor: PASTEL.primarySoft },
                    }}
                >
                    {avatar(32)}
                    {!compact && (
                        <Box sx={{ textAlign: "left", maxWidth: 180, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap lineHeight={1.3}>
                                {identity.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block" lineHeight={1.3}>
                                {identity.appProfileLabel}
                            </Typography>
                        </Box>
                    )}
                    {!compact && <KeyboardArrowDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
                </ButtonBase>
            </Tooltip>
            <Menu
                id="livechat-app-menu"
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={close}
                onClick={close}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                // Di HP lebar menu dibatasi ke layar (sisa 8px tiap sisi, sama dgn marginThreshold),
                // tinggi dibatasi & di-scroll supaya item Logout di bawah tetap terjangkau.
                marginThreshold={8}
                slotProps={{
                    paper: {
                        sx: {
                            width: 300, maxWidth: "calc(100vw - 16px)",
                            maxHeight: "calc(100dvh - 80px)", mt: 0.5,
                        },
                    },
                }}
            >
                <Box sx={{ px: 2, pt: 1, pb: 1.5 }} onClick={(e) => e.stopPropagation()}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        {avatar(40)}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ wordBreak: "break-word" }}>
                                {identity.userName}
                            </Typography>
                            {identity.email && (
                                <Typography variant="caption" color="text.secondary" display="block" noWrap title={identity.email}>
                                    {identity.email}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }} useFlexGap>
                        {identity.role && <Chip size="small" label={`Role: ${identity.role}`} />}
                        {availability.member && (
                            <Chip size="small" color="success" variant="outlined" label="Member Autoleads" />
                        )}
                        {isSpecialist && (
                            <Chip size="small" color="secondary" variant="outlined" label={`Spesialis ${availability.leadProduct}`} />
                        )}
                    </Stack>
                </Box>
                <Divider />
                <Stack spacing={1} sx={{ px: 2, py: 1.5 }} onClick={(e) => e.stopPropagation()}>
                    <InfoRow label="App Profile">
                        <Typography variant="body2" fontWeight={600} sx={{ wordBreak: "break-word" }}>
                            {identity.appProfileLabel}
                        </Typography>
                        {(identity.appProfileWaNumber || identity.appProfileType) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {[identity.appProfileWaNumber, identity.appProfileType].filter(Boolean).join(" · ")}
                            </Typography>
                        )}
                    </InfoRow>
                    <InfoRow label="Brand yang bisa dilihat">
                        {brands.length > 0 ? (
                            <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, mt: 0.25 }} useFlexGap>
                                {brands.map((b) => (
                                    <Chip key={b.brand} size="small" variant="outlined" label={b.brand} sx={{ height: 20, fontSize: 11 }} />
                                ))}
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">Tidak ada</Typography>
                        )}
                    </InfoRow>
                </Stack>
                <Divider />
                {children}
            </Menu>
        </>
    );
}
